import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ExternalLink, Pencil, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { uploadImage } from "@/lib/upload";
import { RichTextEditor } from "@/components/RichTextEditor";

const BLANK = { title: "", slug: "", excerpt: "", content_html: "", cover_image: "", cover_alt: "", tags: [], meta_title: "", meta_description: "", author: "JawaiTrails Team", status: "draft", publish_at: null };
const input = "h-10 rounded-md border border-[#E2DACF] bg-white px-3 text-sm w-full";
const toLocal = (iso) => (iso ? format(new Date(iso), "yyyy-MM-dd'T'HH:mm") : "");

function stateOf(p) {
  if (p.status !== "published") return ["Draft", "bg-[#EFE9E1] text-[#5C6B63]"];
  if (p.publish_at && new Date(p.publish_at) > new Date()) return ["Scheduled", "bg-[#F2C46D] text-[#1C2421]"];
  return ["Published", "bg-[#DCE8DF] text-[#1E3A2B]"];
}

function Field({ label, hint, children }) {
  return <label className="block text-xs text-[#5C6B63] space-y-1"><span>{label}</span>{children}{hint && <span className="block text-[11px]">{hint}</span>}</label>;
}

function CoverPicker({ f, setF }) {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try { setF({ ...f, cover_image: await uploadImage(file) }); toast.success("Cover uploaded"); }
    catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <div className="h-40 rounded-xl bg-[#EFE9E1] overflow-hidden flex items-center justify-center" data-testid="blog-cover-preview">
        {f.cover_image ? <img src={f.cover_image} alt="" className="w-full h-full object-cover"/> : <span className="text-xs text-[#5C6B63]">No cover image</span>}
      </div>
      <label className="jt-pill-outline text-sm cursor-pointer inline-flex"><Upload size={14}/> {busy ? "Uploading…" : "Upload cover"}
        <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="blog-cover-input"/></label>
      <input className={input} placeholder="Cover alt text (describe the image)" value={f.cover_alt} onChange={(e) => setF({ ...f, cover_alt: e.target.value })} data-testid="blog-cover-alt"/>
    </div>
  );
}

function PostEditor({ initial, onDone }) {
  const [f, setF] = useState(initial);
  const [saving, setSaving] = useState(false);
  const save = async (status) => {
    setSaving(true);
    try {
      const body = { ...f, status, publish_at: f.publish_at || null };
      const { data } = f.id ? await api.put(`/admin/blog/${f.id}`, body) : await api.post("/admin/blog", body);
      toast.success(status === "published" ? "Post published" : "Draft saved");
      onDone(data);
    } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  const metaT = f.meta_title || f.title;
  const metaD = f.meta_description || f.excerpt;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]" data-testid="blog-editor">
      <div className="space-y-4">
        <input className="w-full font-serif text-3xl bg-transparent border-b border-[#E2DACF] py-2 focus:outline-none" placeholder="Post title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="blog-title"/>
        <Field label="Summary (shown on the blog list and in Google if no meta description)"><textarea rows={2} maxLength={300} className="w-full rounded-md border border-[#E2DACF] p-3 text-sm" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} data-testid="blog-excerpt"/></Field>
        <RichTextEditor value={f.content_html} onChange={(html) => setF((x) => ({ ...x, content_html: html }))}/>
      </div>
      <aside className="space-y-5">
        <div className="flex gap-2">
          <button onClick={() => save("draft")} disabled={saving || !f.title} className="jt-pill-outline text-sm flex-1 justify-center disabled:opacity-40" data-testid="blog-save-draft">Save draft</button>
          <button onClick={() => save("published")} disabled={saving || !f.title} className="jt-pill-primary text-sm flex-1 justify-center disabled:opacity-40" data-testid="blog-publish">{f.publish_at && new Date(f.publish_at) > new Date() ? "Schedule" : "Publish"}</button>
        </div>
        <button onClick={() => onDone(null)} className="text-xs text-[#5C6B63] underline" data-testid="blog-back">← Back to all posts</button>
        <Field label="Publish on (optional)" hint="Leave empty to publish now. A future date schedules the post.">
          <input type="datetime-local" className={input} value={toLocal(f.publish_at)} onChange={(e) => setF({ ...f, publish_at: e.target.value ? new Date(e.target.value).toISOString() : null })} data-testid="blog-publish-at"/>
        </Field>
        <CoverPicker f={f} setF={setF}/>
        <Field label="Tags (comma separated)"><input className={input} value={f.tags.join(", ")} onChange={(e) => setF({ ...f, tags: e.target.value.split(",").map((t) => t.trimStart()) })} data-testid="blog-tags"/></Field>
        <Field label="Author"><input className={input} value={f.author} onChange={(e) => setF({ ...f, author: e.target.value })} data-testid="blog-author"/></Field>
        <div className="rounded-xl bg-[#F5F0EB] p-4 space-y-3">
          <div className="jt-eyebrow">SEO</div>
          <Field label="URL slug" hint={`/blog/${f.slug || "auto-from-title"}`}><input className={input} value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} data-testid="blog-slug"/></Field>
          <Field label={`Meta title (${metaT.length}/60)`}><input className={input} maxLength={70} value={f.meta_title} placeholder={f.title} onChange={(e) => setF({ ...f, meta_title: e.target.value })} data-testid="blog-meta-title"/></Field>
          <Field label={`Meta description (${metaD.length}/160)`}><textarea rows={3} maxLength={170} className="w-full rounded-md border border-[#E2DACF] p-2 text-sm" value={f.meta_description} placeholder={f.excerpt} onChange={(e) => setF({ ...f, meta_description: e.target.value })} data-testid="blog-meta-desc"/></Field>
          <div className="bg-white rounded-lg p-3" data-testid="blog-serp-preview">
            <div className="text-[11px] text-[#3B4A42] truncate">{window.location.host}/blog/{f.slug || "…"}</div>
            <div className="text-[#1a0dab] text-sm truncate">{metaT || "Post title"} | JawaiTrails</div>
            <div className="text-xs text-[#545454] line-clamp-2">{metaD || "Summary appears here."}</div>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setPosts(r.data)).catch((e) => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);
  const edit = async (p) => { const { data } = await api.get(`/admin/blog/${p.id}`); setEditing(data); };
  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    await api.delete(`/admin/blog/${p.id}`); toast.success("Post deleted"); load();
  };

  if (editing) return <div className="mt-6"><PostEditor initial={editing} onDone={() => { setEditing(null); load(); }}/></div>;
  return (
    <div className="mt-6 space-y-4" data-testid="blog-admin">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#5C6B63]">Published posts appear at /blog and are added to the sitemap automatically.</p>
        <button onClick={() => setEditing(BLANK)} className="jt-pill-primary text-sm" data-testid="blog-new"><Plus size={14}/> New post</button>
      </div>
      <div className="bg-white ring-1 ring-black/5 rounded-2xl divide-y divide-[#EFE9E1]">
        {posts.length === 0 && <p className="p-4 text-sm text-[#5C6B63]">No posts yet.</p>}
        {posts.map((p) => {
          const [label, cls] = stateOf(p);
          return (
            <div key={p.id} className="flex items-center gap-4 p-4" data-testid={`blog-row-${p.slug}`}>
              <div className="w-20 h-14 rounded-lg bg-[#EFE9E1] overflow-hidden shrink-0">{p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover"/>}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">{p.title}</div>
                <div className="text-xs text-[#5C6B63]">/blog/{p.slug} · {p.publish_at ? format(new Date(p.publish_at), "dd MMM yyyy, HH:mm") : "not scheduled"}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${cls}`} data-testid={`blog-status-${p.slug}`}>{label}</span>
              {label === "Published" && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-view-${p.slug}`}><ExternalLink size={15}/></a>}
              <button onClick={() => edit(p)} data-testid={`blog-edit-${p.slug}`}><Pencil size={15}/></button>
              <button onClick={() => remove(p)} data-testid={`blog-delete-${p.slug}`}><Trash2 size={15} className="text-[#C86D51]"/></button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
